import { useMemo } from "react";
import { ROLES } from "roles/roles";
import { useAppSelector } from "redux/hooks";

const useFlowRoles = () => {
    const { KEY_CLOAK_ROLES_SUBS, KEY_CLOAK_ROLES, NEXUS_ROLES } = useAppSelector(e => e.token);

    const isAdminOrSuper = useMemo(
        () => KEY_CLOAK_ROLES.includes(ROLES.ADMIN) || KEY_CLOAK_ROLES.includes(ROLES.SUPER),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    // Leaf codes (ex.: OUTROS_MOVIMENTOS, VENDA_NOVO) chegam em NEXUS_ROLES via strip do último #PART;
    // sub-actions e códigos de seção chegam em KEY_CLOAK_ROLES_SUBS. O Guard consulta as duas fontes,
    // então as flags de ação aqui precisam fazer o mesmo.
    const allRoles = useMemo(
        () => [...KEY_CLOAK_ROLES_SUBS, ...NEXUS_ROLES],
        [KEY_CLOAK_ROLES_SUBS, NEXUS_ROLES],
    );

    const has = (role: ROLES) => isAdminOrSuper || allRoles.includes(role);

    // True when the exact role OR any descendant is present.
    // Used for parent-level UI gates: a user with only ROLE_FLOW#CAIXA#OUTROS_MOVIMENTOS#REFORCAR-CAIXA
    // must still pass the canOutrosMovimentos gate even without the section code.
    const hasOrDescendant = (role: ROLES) =>
        isAdminOrSuper || allRoles.some(r => r === role || r.startsWith(role + '#'));

    // Section cascade: ROLE_FLOW#CAIXA in SUBS means the user has ALL caixa permissions
    const hasCaixaAll = isAdminOrSuper || has(ROLES.CAIXA);
    const hasPreVendaAll = isAdminOrSuper || has(ROLES.PRE_VENDA);
    const hasVendaAll = isAdminOrSuper || has(ROLES.VENDA);
    const hasAcoesVendaAll = hasVendaAll || has(ROLES.ACOES_VENDA);
    const hasConsignadoAll = isAdminOrSuper || has(ROLES.CONSIGNADO);
    const hasAcoesConsignadoAll = hasConsignadoAll || has(ROLES.CONSIGNADO_ACOES);
    const hasGerenciarConsignadoAll = hasConsignadoAll || has(ROLES.CONSIGNADO_GERENCIAR);
    const hasCadastroPessoaAll = isAdminOrSuper || has(ROLES.CADASTRO_PESSOA);

    return {
        caixa: {
            canAbrirCaixa: hasCaixaAll || has(ROLES.CAIXA_ABERTURA),
            canFecharCaixa: hasCaixaAll || has(ROLES.CAIXA_FECHAMENTO),
            canOutrosMovimentos: hasCaixaAll || hasOrDescendant(ROLES.OUTROS_MOVIMENTOS),
            canCancelarValePresente: hasCaixaAll || has(ROLES.OUTROS_MOVIMENTOS) || has(ROLES.OUTROS_MOVIMENTOS_CANCELAR_VALE_PRESENTE),
            canReforcar: hasCaixaAll || has(ROLES.OUTROS_MOVIMENTOS) || has(ROLES.OUTROS_MOVIMENTOS_REFORCAR),
            canSangrar: hasCaixaAll || has(ROLES.OUTROS_MOVIMENTOS) || has(ROLES.OUTROS_MOVIMENTOS_SANGRAR),
            canTransferir: hasCaixaAll || has(ROLES.OUTROS_MOVIMENTOS) || has(ROLES.OUTROS_MOVIMENTOS_TRANSFERIR),
        },

        preVenda: {
            canNovo: hasPreVendaAll || hasOrDescendant(ROLES.PRE_VENDA_NOVO),
            canEditar: hasPreVendaAll || hasOrDescendant(ROLES.PRE_VENDA_EDITAR),
            canAcoes: hasPreVendaAll || hasOrDescendant(ROLES.PRE_VENDA_ACOES),
            canImprimir: hasPreVendaAll || has(ROLES.PRE_VENDA_ACOES) || has(ROLES.PRE_VENDA_ACOES_IMPRIMIR),

            canNovoPrevenda: hasPreVendaAll || has(ROLES.PRE_VENDA_NOVO) || has(ROLES.PRE_VENDA_NOVO_NOVO),
            canNovoDescontoItem: hasPreVendaAll || has(ROLES.PRE_VENDA_NOVO_DESCONTO_ITEM),
            canNovoDescontoRodape: hasPreVendaAll || has(ROLES.PRE_VENDA_NOVO_DESCONTO_RODAPE),
            canNovoAcrescimo: hasPreVendaAll || has(ROLES.PRE_VENDA_NOVO_ACRESCIMO),
            canNovoBrinde: hasPreVendaAll || has(ROLES.PRE_VENDA_NOVO_BRINDE),
            canNovoFinalizar: hasPreVendaAll || has(ROLES.PRE_VENDA_NOVO_FINALIZAR),

            canNovoValePresente:    hasPreVendaAll || has(ROLES.PRE_VENDA_NOVO) || has(ROLES.PRE_VENDA_NOVO_VALE_PRESENTE),
            canNovoObservacao:      hasPreVendaAll || has(ROLES.PRE_VENDA_NOVO) || has(ROLES.PRE_VENDA_NOVO_OBSERVACAO),
            canNovoConsultaEstoque: hasPreVendaAll || has(ROLES.PRE_VENDA_NOVO) || has(ROLES.PRE_VENDA_NOVO_CONSULTA_ESTOQUE),

            canEditarPrevenda: hasPreVendaAll || has(ROLES.PRE_VENDA_EDITAR) || has(ROLES.PRE_VENDA_EDITAR_EDITAR),
            canDescontoItem: hasPreVendaAll || has(ROLES.PRE_VENDA_EDITAR_DESCONTO_ITEM),
            canDescontoRodape: hasPreVendaAll || has(ROLES.PRE_VENDA_EDITAR_DESCONTO_RODAPE),
            canAcrescimo: hasPreVendaAll || has(ROLES.PRE_VENDA_EDITAR_ACRESCIMO),
            canBrinde: hasPreVendaAll || has(ROLES.PRE_VENDA_EDITAR_BRINDE),
            canFinalizar: hasPreVendaAll || has(ROLES.PRE_VENDA_EDITAR_FINALIZAR),
            canValePresente:    hasPreVendaAll || has(ROLES.PRE_VENDA_EDITAR) || has(ROLES.PRE_VENDA_EDITAR_VALE_PRESENTE),
            canObservacao:      hasPreVendaAll || has(ROLES.PRE_VENDA_EDITAR) || has(ROLES.PRE_VENDA_EDITAR_OBSERVACAO),
            canConsultaEstoque: hasPreVendaAll || has(ROLES.PRE_VENDA_EDITAR) || has(ROLES.PRE_VENDA_EDITAR_CONSULTA_ESTOQUE),
        },

        venda: {
            canAcessarPDV: hasOrDescendant(ROLES.VENDA),
            canNovo: hasVendaAll || hasOrDescendant(ROLES.VENDA_NOVO),
            canNovoPedido: hasVendaAll || has(ROLES.VENDA_NOVO) || has(ROLES.VENDA_NOVO_PEDIDO),
            canNovoDescontoItem: hasVendaAll || has(ROLES.VENDA_NOVO) || has(ROLES.VENDA_NOVO_DESCONTO_ITEM),
            canNovoDescontoRodape: hasVendaAll || has(ROLES.VENDA_NOVO) || has(ROLES.VENDA_NOVO_DESCONTO_RODAPE),
            canNovoAcrescimo: hasVendaAll || has(ROLES.VENDA_NOVO) || has(ROLES.VENDA_NOVO_ACRESCIMO),
            canNovoBrinde: hasVendaAll || has(ROLES.VENDA_NOVO) || has(ROLES.VENDA_NOVO_BRINDE),
            canNovoDadosFrete: hasVendaAll || has(ROLES.VENDA_NOVO) || has(ROLES.VENDA_NOVO_DADOS_FRETE),
            canNovoValePresente:    hasVendaAll || has(ROLES.VENDA_NOVO) || has(ROLES.VENDA_NOVO_VALE_PRESENTE),
            canNovoObservacao:      hasVendaAll || has(ROLES.VENDA_NOVO) || has(ROLES.VENDA_NOVO_OBSERVACAO),
            canNovoConsultaEstoque: hasVendaAll || has(ROLES.VENDA_NOVO) || has(ROLES.VENDA_NOVO_CONSULTA_ESTOQUE),
            canEditar: hasVendaAll || hasOrDescendant(ROLES.VENDA_EDITAR),
            canEditarPedido: hasVendaAll || has(ROLES.VENDA_EDITAR) || has(ROLES.VENDA_EDITAR_PEDIDO),
            canDescontoItem: hasVendaAll || has(ROLES.VENDA_EDITAR) || has(ROLES.VENDA_EDITAR_DESCONTO_ITEM),
            canDescontoRodape: hasVendaAll || has(ROLES.VENDA_EDITAR) || has(ROLES.VENDA_EDITAR_DESCONTO_RODAPE),
            canAcrescimo: hasVendaAll || has(ROLES.VENDA_EDITAR) || has(ROLES.VENDA_EDITAR_ACRESCIMO),
            canBrinde: hasVendaAll || has(ROLES.VENDA_EDITAR) || has(ROLES.VENDA_EDITAR_BRINDE),
            canDadosFrete: hasVendaAll || has(ROLES.VENDA_EDITAR) || has(ROLES.VENDA_EDITAR_DADOS_FRETE),
            canEditarVendedor: hasVendaAll || has(ROLES.VENDA_EDITAR) || has(ROLES.VENDA_EDITAR_VENDEDOR),
            canValePresente:    hasVendaAll || has(ROLES.VENDA_EDITAR) || has(ROLES.VENDA_EDITAR_VALE_PRESENTE),
            canObservacao:      hasVendaAll || has(ROLES.VENDA_EDITAR) || has(ROLES.VENDA_EDITAR_OBSERVACAO),
            canConsultaEstoque: hasVendaAll || has(ROLES.VENDA_EDITAR) || has(ROLES.VENDA_EDITAR_CONSULTA_ESTOQUE),

            pagamento: {
                canSalvar: hasVendaAll || has(ROLES.VENDA_SALVAR_PEDIDO),
                canFinalizar: hasVendaAll || has(ROLES.VENDA_FINALIZAR),
                canFinalizarOutros: hasVendaAll || has(ROLES.VENDA_FINALIZAR_OUTROS),
            },

            acoes: {
                canEditarVendedor: hasAcoesVendaAll || has(ROLES.VENDA_ACOES_EDITAR_VENDEDOR),
                // Direct actions
                canVisualizar: hasAcoesVendaAll || has(ROLES.VENDA_ACOES_VISUALIZAR),
                canConferir: hasAcoesVendaAll || has(ROLES.VENDA_ACOES_CONFERIR),
                canCancelarPedido: hasAcoesVendaAll || has(ROLES.VENDA_ACOES_CANCELAR_PEDIDO),
                canCancelarDfe: hasAcoesVendaAll || has(ROLES.VENDA_ACOES_CANCELAR_DFE),
                canAgrupar: hasAcoesVendaAll || has(ROLES.VENDA_ACOES_AGRUPAR),
                canBaixarXml: hasAcoesVendaAll || has(ROLES.VENDA_ACOES_BAIXAR_XML),
                canExcluirNota: hasAcoesVendaAll || has(ROLES.VENDA_ACOES_EXCLUIR_NOTA),
                canAtualizarSituacao: hasAcoesVendaAll || has(ROLES.VENDA_ACOES_ATUALIZAR_SITUACAO),
                // Imprimir leaf + sub-actions
                canImprimir: hasAcoesVendaAll || hasOrDescendant(ROLES.VENDA_ACOES_IMPRIMIR),
                canImprimirPreVenda: hasAcoesVendaAll || has(ROLES.VENDA_ACOES_IMPRIMIR) || has(ROLES.VENDA_ACOES_IMPRIMIR_PRE_VENDA),
                canImprimirPedido: hasAcoesVendaAll || has(ROLES.VENDA_ACOES_IMPRIMIR) || has(ROLES.VENDA_ACOES_IMPRIMIR_PEDIDO),
                canImprimirDanfe: hasAcoesVendaAll || has(ROLES.VENDA_ACOES_IMPRIMIR) || has(ROLES.VENDA_ACOES_IMPRIMIR_DANFE),
                canImprimirRomaneio: hasAcoesVendaAll || has(ROLES.VENDA_ACOES_IMPRIMIR) || has(ROLES.VENDA_ACOES_IMPRIMIR_ROMANEIO),
                canImprimirEtiquetaVolume: hasAcoesVendaAll || has(ROLES.VENDA_ACOES_IMPRIMIR) || has(ROLES.VENDA_ACOES_IMPRIMIR_ETIQUETA_VOLUME),
                canImprimirCrediario: hasAcoesVendaAll || has(ROLES.VENDA_ACOES_IMPRIMIR) || has(ROLES.VENDA_ACOES_IMPRIMIR_CREDIARIO),
                canImprimirEtiquetaSimplificada: hasAcoesVendaAll || has(ROLES.VENDA_ACOES_IMPRIMIR) || has(ROLES.VENDA_ACOES_IMPRIMIR_ETIQUETA_SIMPLIFICADA),
            },
        },

        consignado: {
            canNovo: hasConsignadoAll || has(ROLES.CONSIGNADO_NOVO),

            // Ações — list-level
            canImprimir:       hasAcoesConsignadoAll || has(ROLES.CONSIGNADO_ACOES_IMPRIMIR),
            canMovimentacoes:  hasAcoesConsignadoAll || has(ROLES.CONSIGNADO_ACOES_MOVIMENTACOES),
            canCancelar:       hasAcoesConsignadoAll || has(ROLES.CONSIGNADO_ACOES_CANCELAR),
            canDevolucaoAcoes:    hasAcoesConsignadoAll || has(ROLES.CONSIGNADO_ACOES_DEVOLUCAO),
            canGerarVendaAcoes:   hasAcoesConsignadoAll || has(ROLES.CONSIGNADO_ACOES_GERAR_VENDA),

            // Gerenciar — detail-level
            canEditar:            hasConsignadoAll || hasOrDescendant(ROLES.CONSIGNADO_GERENCIAR),
            canEditarConsignado:  hasGerenciarConsignadoAll || has(ROLES.CONSIGNADO_GERENCIAR_EDITAR),
            canDescontoItem:      hasGerenciarConsignadoAll || has(ROLES.CONSIGNADO_GERENCIAR_DESCONTO_ITEM),
            canConsignar:         hasGerenciarConsignadoAll || has(ROLES.CONSIGNADO_GERENCIAR_CONSIGNAR),
            canConsignarOutros:   hasGerenciarConsignadoAll || has(ROLES.CONSIGNADO_GERENCIAR_CONSIGNAR_OUTROS),
            canDevolucaoGerenciar:  hasGerenciarConsignadoAll || has(ROLES.CONSIGNADO_GERENCIAR_DEVOLUCAO),
            canGerarVendaGerenciar: hasGerenciarConsignadoAll || has(ROLES.CONSIGNADO_GERENCIAR_GERAR_VENDA),
        },

        cadastroPessoa: {
            canNovo: hasCadastroPessoaAll || hasOrDescendant(ROLES.CADASTRO_PESSOA_NOVO),
            canEditar: hasCadastroPessoaAll || hasOrDescendant(ROLES.CADASTRO_PESSOA_EDITAR),
            novo: {
                canPapel: hasCadastroPessoaAll || has(ROLES.CADASTRO_PESSOA_NOVO) || has(ROLES.CADASTRO_PESSOA_NOVO_PAPEL),
                canVendedor: hasCadastroPessoaAll || has(ROLES.CADASTRO_PESSOA_NOVO) || has(ROLES.CADASTRO_PESSOA_NOVO_VENDEDOR),
                canGuia: hasCadastroPessoaAll || has(ROLES.CADASTRO_PESSOA_NOVO) || has(ROLES.CADASTRO_PESSOA_NOVO_GUIA),
            },
            editar: {
                canPapel: hasCadastroPessoaAll || has(ROLES.CADASTRO_PESSOA_EDITAR) || has(ROLES.CADASTRO_PESSOA_EDITAR_PAPEL),
                canVendedor: hasCadastroPessoaAll || has(ROLES.CADASTRO_PESSOA_EDITAR) || has(ROLES.CADASTRO_PESSOA_EDITAR_VENDEDOR),
                canGuia: hasCadastroPessoaAll || has(ROLES.CADASTRO_PESSOA_EDITAR) || has(ROLES.CADASTRO_PESSOA_EDITAR_GUIA),
            },
        },
    };
};

export default useFlowRoles;
